import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const COMPLETION_DELAY = 500

// Builds the same three markup shapes the old string templates produced, as DOM nodes rather than
// an innerHTML-assigned string, so a value arriving off the wire (the ActionCable `url`) can never
// be interpreted as markup. `this.element.replaceChildren(...)` replaces the holder's contents the
// same way the innerHTML assignments it replaces did.
const buildExportingProgress = (progress) => {
  const button = document.createElement("button")
  button.className = "btn btn-main exporting-button"
  button.disabled = true
  button.dataset.progress = String(progress)

  const label = document.createElement("span")
  label.textContent = "Exporting..."

  button.appendChild(label)
  return button
}

const buildExportLink = (url) => {
  const link = document.createElement("a")
  link.className = "btn btn-main"
  link.id = "export-button"
  link.setAttribute("href", url)
  link.textContent = "Export Data"
  return link
}

const buildDownloadLink = (url) => {
  const link = document.createElement("a")
  link.target = "_blank"
  link.id = "download-button"
  link.className = "btn btn-primary btn-ghost"
  link.setAttribute("href", url)
  link.textContent = "Download Exported Data"
  return link
}

export default class extends Controller {
  initialize() {
    this.onClick = this.onClick.bind(this)
  }

  connect() {
    // Document-wide: the campaign page renders this partial twice with the same ids, and the
    // campaign is the exportable for both, so either copy answers with the same id.
    const button = document.querySelector("#export-button, #download-button")
    this.exportableId = button ? button.getAttribute("data-exportable-id") : undefined

    // Read once, here: the dashboard rewrites the address bar shortly after this runs, so reading
    // the range at click time would report a different one.
    this.range = new URLSearchParams(window.location.search).get("range")

    this.element.addEventListener("click", this.onClick)

    const exportEvent = this.currentExport()
    if (exportEvent && (exportEvent.stage === "waiting" || exportEvent.stage === "in_progress")) {
      this.subscribeToExport(exportEvent.id)
    }
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick)
  }

  // Delegated from the mount so both clicks survive the innerHTML swaps below.
  onClick(event) {
    if (event.target.closest("#export-button")) {
      this.start(event)
    } else if (event.target.closest("#download-button")) {
      this.resetToExportLink()
    }
  }

  start(event) {
    event.preventDefault()
    // campaign_gift_customer_export_controller.js delegates the same click and would otherwise
    // rewrite the first #export-holder on the page, which is its own.
    event.stopPropagation()

    const params = $.param({ range: this.range, exportable_id: this.exportableId })
    const url = `${event.target.closest("#export-button").getAttribute("href")}?${params}`

    this.element.replaceChildren(buildExportingProgress(0))

    const options = RequestHelper.defaultRequestOptions()
    options.headers = Object.assign({}, options.headers, {
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*"
    })

    fetch(url, options).
      then(response => {
        if (!response.ok) { throw new Error(`Export request failed (${response.status})`) }
        return response.json()
      }).
      then(body => {
        // An object body has no length, so this guard always passes.
        if (body.length !== 0) { this.subscribeToExport(body.export_id) }
      }).
      catch(this.logFailedCall)
  }

  resetToExportLink() {
    this.element.replaceChildren(buildExportLink(this.element.getAttribute("data-export-url")))
  }

  // `data-export` carries the ExportEvent as JSON, the same shape jQuery's `.data()` used to
  // parse for us; `data-export-url` is a plain string, read as-is. Both stay their existing
  // attribute names -- this partial's `stimulus_controller` local is also mounted by
  // scheduler-customer-export (see decisions.md D12), so hard-coding a Values-API-shaped
  // attribute name here would break that mount.
  currentExport() {
    const raw = this.element.getAttribute("data-export")
    return raw ? JSON.parse(raw) : null
  }

  subscribeToExport(exportId) {
    const subscription = App.cable.subscriptions.create(
      { channel: "CampaignDomainPerformanceExportsChannel", id: exportId },
      { received: (data) => this.receive(subscription, data) }
    )
  }

  receive(subscription, data) {
    const button = this.element.querySelector(".exporting-button")
    // In case subscription messages come in a weird order, we keep the highest progress
    const currentProgress = parseInt(button ? button.dataset.progress : undefined) || 0
    const newProgress = currentProgress >= (parseInt(data.event.progress) || 0) ? currentProgress : data.event.progress

    if (data.event.stage === "in_progress" || data.event.stage === "waiting") {
      this.paintProgress(`${100 - newProgress}% bottom`)
    } else if (data.event.stage === "complete") {
      subscription.unsubscribe()
      this.paintProgress("0% bottom")
      setTimeout(() => {
        this.element.replaceChildren(buildDownloadLink(data.event.url))
      }, COMPLETION_DELAY)
    }
  }

  // querySelectorAll + forEach rather than a target, because the holder legitimately renders no
  // exporting button at all and jQuery's set operations no-opped over that. Also not a target
  // because the shared partial (shared/lasso/dashboard/_export_button, decisions.md D12)
  // server-renders this same `.exporting-button` markup directly on an initial "in_progress" page
  // load, without any controller-specific target attribute -- a Target would go stale on that path
  // (pitfalls.md P8).
  paintProgress(position) {
    this.element.querySelectorAll(".exporting-button, .exporting-button span").
      forEach(element => { element.style.backgroundPosition = position })
  }

  logFailedCall(response) {
    console.warn(response)
  }
}
